import { theme } from '@contrast-security-inc/design-system-foundations'
import { css } from '@emotion/css'
import React, { useState } from 'react'
import { theme as deprecatedTheme } from '../../styled'
import { AgentLanguage } from '../agent-language'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import { CsVulnerabilityIcon } from '../../legacy-design-system/icons'
import { CollapsibleListToggle } from '../../legacy-design-system/list'
import { getApplicationLibrariesWithPanelOpenRoute } from '../route'
import { WithTooltip } from '../../design-system'
import { Link } from '@contrast-security-inc/design-system-typography'

interface BaseLibrary {
  readonly name: string
  readonly hash: string
}

interface Library extends BaseLibrary {
  readonly vulnerable?: boolean
}

interface ApplicationTechnologyItemProps {
  readonly language: AgentLanguage
  readonly organizationID: string
  readonly technologyName: string
  readonly libraries: Library[]
  readonly applicationID: string
}

export function ApplicationTechnologyItem({
  language,
  technologyName,
  libraries,
  organizationID,
  applicationID
}: ApplicationTechnologyItemProps) {
  const librariesCves = libraries.filter((library) => !!library.vulnerable)

  const height = 40
  const borderRadius = 6

  return (
    <div
      className={css`
        display: flex;
        flex: 1;
        height: ${height}px;
        min-height: ${height}px;
        max-height: ${height}px;
        padding: 10px;
        background-color: ${theme.color.neutral10};
        border-bottom: 1px solid ${theme.color.neutral40};
        justify-content: space-between;
        flex-direction: row;
        display: flex;

        &:hover {
          z-index: 2;
          box-shadow: 0px 3px 4px 0px ${theme.color.neutral40};
        }
        :last-child {
          border-radius: 0 0 ${borderRadius}px ${borderRadius}px;
          border-bottom: 0px solid ${theme.color.transparent};
        }
        :first-child {
          border-radius: ${borderRadius}px ${borderRadius}px 0 0;
        }
        :last-child:first-child {
          border-radius: ${borderRadius}px ${borderRadius}px ${borderRadius}px
            ${borderRadius}px;
          border-bottom: 0px solid ${theme.color.transparent};
        }
      `}
    >
      {Array.isArray(libraries) && libraries.length > 0 ? (
        <div>
          <ContrastFormattedMessage
            id="detail_applicationTechnology_nLibraryInstances"
            values={{ count: libraries.length }}
          >
            {(tooltipTitle) => (
              <WithTooltip
                placement="right"
                renderTooltipContent={() => (
                  <ApplicationTechnologyTooltipContent
                    language={language}
                    organizationID={organizationID}
                    libraries={libraries}
                    title={tooltipTitle}
                    applicationID={applicationID}
                  />
                )}
                children={() => (
                  <span
                    className={css`
                      &:hover {
                        text-decoration: underline;
                      }
                    `}
                  >
                    {technologyName}
                  </span>
                )}
              />
            )}
          </ContrastFormattedMessage>
        </div>
      ) : (
        technologyName
      )}

      {Array.isArray(librariesCves) && librariesCves.length > 0 && (
        <div>
          <ContrastFormattedMessage
            id="detail_applicationTechnology_nLibraryInstancesWithCVEs"
            values={{ count: librariesCves.length }}
          >
            {(tooltipTitle) => (
              <WithTooltip
                placement="right"
                renderTooltipContent={() => (
                  <ApplicationTechnologyTooltipContent
                    language={language}
                    organizationID={organizationID}
                    libraries={librariesCves}
                    title={tooltipTitle}
                    applicationID={applicationID}
                  />
                )}
                children={() => (
                  <span>
                    <CsVulnerabilityIcon
                      data-testid="cve_icon_indicator"
                      color={theme.color.neutral60}
                      size={18}
                    />
                  </span>
                )}
              />
            )}
          </ContrastFormattedMessage>
        </div>
      )}
    </div>
  )
}

function ApplicationTechnologyTooltipContent({
  language,
  libraries,
  title,
  organizationID,
  applicationID
}: {
  readonly language: AgentLanguage
  readonly libraries: BaseLibrary[]
  readonly title: string
  readonly organizationID: string
  readonly applicationID: string
}) {
  return (
    <div>
      <div
        className={css`
          margin-bottom: 4px;
          white-space: nowrap;
        `}
      >
        <span
          className={css`
            font-weight: ${deprecatedTheme.fontWeights.primary.bold};
            margin-right: 3px;
          `}
        >
          {libraries.length}
        </span>
        {title}
      </div>
      <LibraryList
        libraries={libraries}
        organizationID={organizationID}
        language={language}
        applicationID={applicationID}
      />
    </div>
  )
}

function LibraryList({
  language,
  libraries,
  organizationID,
  applicationID
}: {
  readonly language: AgentLanguage
  readonly libraries: BaseLibrary[]
  readonly organizationID: string
  readonly applicationID: string
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const visible = isExpanded ? libraries : libraries.slice(0, 5)

  return (
    <div>
      <ul
        className={css`
          list-style: none;
          padding: 0;
          margin: 0;
        `}
        data-testid="source-names-ip-addresses-cell"
      >
        {visible.map(({ name, hash }, index) => (
          <li key={index}>
            <Link
              href={getApplicationLibrariesWithPanelOpenRoute(
                organizationID,
                applicationID,
                hash,
                language.getValue(),
                name
              )}
              className={css`
                color: ${theme.color.turquoise50} !important;
              `}
              isExternal
            >
              {name}
            </Link>
          </li>
        ))}
      </ul>
      {libraries.length > 5 && (
        <CollapsibleListToggle
          isExpanded={isExpanded}
          onToggle={(_isExpanded) => {
            if (isExpanded !== _isExpanded) {
              setIsExpanded(_isExpanded)
            }
          }}
        />
      )}
    </div>
  )
}
